import { BodyText } from "@/components/ui/Text";
import { cn } from "@/lib/utils";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

interface NavItem {
  icon: React.ElementType;
  label: string;
  to: string;
  /**
   * Optional visual cue rendered on the route's icon (e.g. a small dot to
   * signal "there's something new to look at here"). Kept as a boolean so
   * each consumer decides what truthiness means.
   */
  indicator?: boolean;
}

interface SidebarProps {
  className?: string;
  routes: NavItem[];
}

export function Sidebar({ routes }: SidebarProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const toggleSidebar = () => setIsOpen((prev) => !prev);

  return (
    <div className="relative h-full">
      <div
        className={cn(
          "h-full bg-white border-r py-3  overflow-y-auto",
          isOpen ? "absolute z-20 w-40 transition-all duration-100" : "w-10",
        )}
      >
        <nav
          className="flex flex-col gap-1"
          onMouseEnter={toggleSidebar}
          onMouseLeave={toggleSidebar}
        >
          {routes.map((route) => {
            return (
              <Link
                to={route.to}
                search
                className="flex items-center justify-start transition-all duration-100"
                from="/patient/$patientId"
                key={route.label}
                aria-label={route.label}
              >
                {({ isActive }) => {
                  return (
                    <div
                      className={`${isActive && "bg-primary-green/20"} w-full flex items-center justify-start gap-2 rounded-lg py-1 px-1 mx-2`}
                    >
                      <div className="w-4 flex items-center justify-center relative">
                        <route.icon
                          className={cn(
                            "h-4 w-4 stroke-link",
                            isActive ? "stroke-[3px]" : "stroke-2",
                          )}
                        />
                        {route.indicator && (
                          <span
                            aria-hidden
                            className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-button-superclinical border border-white"
                          />
                        )}
                      </div>
                      <BodyText
                        size="sm"
                        textColor="link"
                        wrap={false}
                        className={cn(
                          "cursor-pointer",
                          isOpen ? "" : "hidden",
                          isActive ? "font-bold" : "font-normal",
                        )}
                      >
                        {route.label}
                      </BodyText>
                    </div>
                  );
                }}
              </Link>
            );
          })}
        </nav>
      </div>
      <div className="w-10 h-full" />
    </div>
  );
}
